import dayjs from 'dayjs';
import React from 'react';
import parse, { attributesToProps, domToReact } from 'html-react-parser';
import MarkdownIt from 'markdown-it';
import { renderFieldLabel } from '../../../widgets/utilities/helperUtility';

const Utils = {
  capitalize(value) {
    return value.replace('_', ' ').replace(/(^|\s)\S/g, (l) => l.toUpperCase());
  },
  lowerCase(value) {
    return value.replace('_', ' ').toLowerCase();
  },
  sentenceCase(value) {
    return value.charAt(0).toUpperCase() + value.replace('_', ' ').slice(1).toLowerCase();
  },
  classStyle(props, defaultClassStyle) {
    let style = props.classStyle ? props.classStyle : defaultClassStyle;
    if (props.errorClassStyle) {
      style += ` ${props.errorClassStyle}`;
    }
    return style;
  },
  errorClassForIndex(errors, index, field) {
    if (errors[index] && errors[index][field]) {
      return 'error';
    }
    return '';
  },
  displayForProfiles(profiles, profile) {
    return profiles.indexOf(profile) > -1;
  },
  hideForProfiles(profiles, profile) {
    return !this.displayForProfiles(profiles, profile);
  },
  gup(name) {
    // grab url param
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const results = new RegExp(`[\\?&]${name}=([^&#]*)`).exec(location.href);
    return results == null ? null : results[1];
  },
  isBlank(val) {
    return val === 'NULL' || val === '' || val == null;
  },
  isNewPage(page) {
    return location.href.includes(`${page}/new`);
  },
  parseInt(value, defaultValue) {
    return this.isBlank(value) ? defaultValue : parseInt(value);
  },
  timeFormat() {
    const locale = navigator.language || navigator.userLanguage;
    return locale.substr(0, 2) === 'en' ? 'h:mm a' : 'H:mm';
  },
  timeUpperFormat() {
    const locale = navigator.language || navigator.userLanguage;
    return locale.substr(0, 2) === 'en' ? 'h:mm A' : 'H:mm';
  },
  momentDateFormat() {
    return GLOBAL_VAR.current_shop.settings.date_format.toLowerCase().toUpperCase();
  },
  momentDateTimeFormat() {
    return `${this.momentDateFormat()} ${this.timeFormat()}`;
  },

  momentDateTimeUpperFormat() {
    return `${this.momentDateFormat()} ${this.timeUpperFormat()}`;
  },
  dateFormat(format = null) {
    if (typeof GLOBAL_VAR === 'undefined') {
      return format.toLowerCase().replace('mm', 'MM');
    }
    return GLOBAL_VAR.current_shop.settings.date_format.toLowerCase().replace('mm', 'MM');
  },
  dateTimeFormat(format) {
    return `${this.dateFormat(format)} ${this.timeFormat()}`;
  },
  timelineTimeFormat() {
    const locale = navigator.language || navigator.userLanguage;
    return locale.startsWith('en') ? 'h:mm A' : 'HH:mm';
  },
  timelineDateTimeFormat() {
    return `MMM DD, YYYY, ${this.timelineTimeFormat()}`;
  },
  timeInterval(tInterval = null) {
    let interval = tInterval;
    if (typeof GLOBAL_VAR !== 'undefined') {
      interval = +GLOBAL_VAR.current_shop.settings.datepicker_time_interval;
    }
    if (!interval || interval == 0) interval = 15;
    return interval;
  },
  savingFormat() {
    return 'YYYY-MM-DD HH:mm';
  },
  validHostname(host) {
    const pattern = new RegExp(/^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/); // RFC 1123
    return !!pattern.test(host);
  },
  validURL(url) {
    const pattern = new RegExp('^(https?:\\/\\/)?' // protocol
      + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
      + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
      + '(\\:\\d+)?(\\/[-a-z\\d%@_.~+]*)*' // port and path
      + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
      + '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
    return !!pattern.test(url);
  },
  validEmail(email) {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email.toLowerCase());
  },
  validSeason(season, events) {
    const errors = [];
    if (!season.name) errors.push({ key: 'name', value: "Season name can't be blank", fieldChanged: false });
    if (season.start === 'Invalid Date') errors.push({ key: 'start', value: 'Invalid start value', fieldChanged: false });
    if (season.finish === 'Invalid Date') errors.push({ key: 'finish', value: 'Invalid finish value', fieldChanged: false });
    if (events.some((event) => {
      return event.end_hour !== '00:00' && event.start_hour > event.end_hour;
    })) {
      errors.push({ key: 'hours', value: "close time can't be less than open time", fieldChanged: false });
    }
    return errors;
  },
  buildOptionsForSelect(array, titleField, blankOption = false) {
    const options = blankOption ? [{ label: blankOption, value: 0 }] : [];
    return options.concat(array.map((element) => ({ label: element[titleField], value: element.id })));
  },
  buildDateRange() {
    return {
      Today: [dayjs(), dayjs()],
      Tomorrow: [dayjs().add(1, 'days'), dayjs().add(1, 'days')],
      'Next 7 Days': [dayjs(), dayjs().add(6, 'days')],
      'Next 30 Days': [dayjs(), dayjs().add(29, 'days')],
      'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
      'Next Month': [dayjs().add(1, 'month').startOf('month'), dayjs().add(1, 'month').endOf('month')],
    };
  },
  eventTimeFormat() {
    const format = {
      hour: 'numeric',
      minute: '2-digit',
    };

    if (window.bta.timeFormat === 'h:mm a') {
      format.hour12 = true;
      format.meridiem = 'short';
    } else {
      format.hour12 = false;
    }

    return format;
  },
  combineRules(resourceOptions, entry, root = false) {
    if (entry.rules !== undefined) {
      const rules = entry.rules.map((rule) => this.combineRules(resourceOptions, rule));
      const result = rules.join(` ${entry.combinator.toLocaleUpperCase()} `);
      return root ? result : `(${result})`;
    }

    if (entry.value && resourceOptions) {
      // eslint-disable-next-line no-unused-vars
      const resource = resourceOptions.find(([_name, id]) => parseInt(id, 10) === parseInt(entry.value, 10));
      return resource && resource[0];
    }

    return 'Unassigned';
  },
  convertMarkdownLabelToReactHtml(label) {
    const markdown = new MarkdownIt();
    const domParseOptions = {
      replace: ({ name, attribs, children }) => {
        if (name === 'a') {
          const props = attributesToProps(attribs);
          const reg = new RegExp(/^((http|https|ftp):\/\/)/);
          const { href } = props;
          if (!reg.test(href)) {
            props.href = `https://${href}`;
          }
          return (
            <a target="_blank" rel="noopener noreferrer" {...props}>
              {domToReact(children, domParseOptions)}
            </a>
          );
        }
      },
    };

    return parse(renderFieldLabel(label, markdown), domParseOptions);
  },
};

export default Utils;
